<template>
  <div>
    <!-- Sections -->
    <transition name="slide-fade" mode="out-in">
      <div v-if="!settingsActive" class="sections">
        <Container
          v-show="sections && sections.length"
          :key="sections.length"
          lock-axis="y"
          @drop="onDrop"
        >
          <Draggable v-for="(section, index) in sections" :key="index">
            <Section
              :name="section.name"
              :section-id="section.sectionId"
              @section-click="onSectionClick"
            />
          </Draggable>
        </Container>
      </div>

      <!-- Section Settings -->
      <SectionSettings
        v-if="settingsActive"
        :section-id="activeSectionId"
        :section-name="activeSectionName"
        :campaign-id="campaign.campaignId"
        @close-section="onCloseSection"
        @remove-section="onRemoveSection"
      />
    </transition>
  </div>
</template>

<script>
import { Container, Draggable } from "vue-smooth-dnd";
import { ReorderSections } from "@/graphql/mutations";
import { Campaign } from "@/graphql/queries";
import Section from "@/components/Editor/Layout/Sections/Section";
import SectionSettings from "@/components/Editor/Layout/Sections/SectionSettings";
import EventBus from "@/lib/event-bus.js";

export default {
  name: "LayoutEditor",
  components: {
    Container,
    Draggable,
    Section,
    SectionSettings,
  },
  props: {
    campaign: { default: null, type: Object },
    settingsActive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeSectionId: "",
      activeSectionName: "",
      orderedSections: [],
    };
  },
  computed: {
    sections() {
      if (this.orderedSections.length) {
        return this.orderedSections;
      } else if (
        this.campaign?.template?.sections &&
        this.campaign?.template?.sections.length
      ) {
        return this.campaign?.template?.sections;
      } else {
        return [];
      }
    },
  },
  methods: {
    applyDrag(arr, dragResult) {
      if (dragResult.addedIndex === dragResult.removedIndex) return [];

      const { removedIndex, addedIndex, payload } = dragResult;
      if (removedIndex === null && addedIndex === null) return arr;

      const result = [...arr];
      let itemToAdd = payload;

      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
      }

      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      return result;
    },
    formatSectionsForMutation() {
      return this.sections.map((section, index) => ({
        order: index,
        sectionId: section.sectionId,
      }));
    },
    onDrop: async function(dropResult) {
      // Reorder the sections
      this.orderedSections = this.applyDrag(this.sections, dropResult);

      // Skip if no reorder happened
      if (!this.orderedSections.length) return false;

      // Emit the preview loading event to put the Preview component in it's loading state...
      EventBus.$emit("preview-loading", true);

      this.$gtm.trackEvent({
        event: "promo-section-reorder",
        campaignId: this.campaign?.campaignId,
      });

      const reorderSectionsInput = {
        input: {
          sections: this.formatSectionsForMutation(),
          campaignId: this.campaign?.campaignId,
        },
      };

      try {
        // Send the mutation
        await this.$apollo.mutate({
          mutation: ReorderSections,
          variables: reorderSectionsInput,
          // Update the cache with the result
          // The query will be updated with the optimistic response
          // and then with the real result of the mutation
          update: (store, { data: { reorderSections } }) => {
            this.updateCampaign(store, reorderSections);

            EventBus.$emit("refetch-preview");
          },
        });
      } catch (error) {
        this.$modal.show("dialog", {
          title: "Oh no 😯",
          text: `Sorry but there was an error re-ordering the sections. Please try again. If the problem persists please contact our <a href="mailto:support@promo.ai?subject=Promo.ai Error: Reorder Section&body=%0D%0A%0D%0AError: ${error}">support team</a>.`,
          buttons: [
            {
              title: "Close",
            },
          ],
        });
      }
    },
    onCloseSection() {
      if (this.settingsActive) this.$emit("update:settings-active", false);

      this.activeSectionId = "";
      this.activeSectionName = "";
    },
    onRemoveSection() {
      // Wait till the campaign has updated from updateCampaign() in SectionSettings...
      this.$nextTick(() => {
        // Then close the section...
        this.onCloseSection();
      });
    },
    onSectionClick(sectionId, sectionName) {
      this.$emit("update:settings-active", true);
      this.activeSectionId = sectionId;
      this.activeSectionName = sectionName;
    },
    updateCampaign(store, reorderSections) {
      const campaignInput = {
        input: {
          campaignId: this.campaign?.campaignId,
        },
      };

      // Read the data from our cache for this query.
      const data = store.readQuery({
        query: Campaign,
        variables: campaignInput,
      });

      // Update the cache with the response
      data.campaign.template.sections = reorderSections.template?.sections;

      // Write our data back to the cache.
      store.writeQuery({
        query: Campaign,
        variables: campaignInput,
        data,
      });
    },
  },
};
</script>

<style scoped>
>>> .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
  overflow: visible;
}

.sections {
  @apply p-4 pb-0;
}
</style>
